import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, ShieldCheck, FileCheck2, Radar, Lock, Layers, Gauge, Server, Building2, Users, Eye, Factory } from "lucide-react";
import SEO from "../components/SEO";

const FRAMEWORKS = [
  { code: "NIST", name: "NIST 800-53 Rev 5", levels: "Security & Privacy Controls", desc: "Baseline control catalog for federal information systems.", href: "https://csrc.nist.gov/publications/detail/sp/800-53/rev-5/final" },
  { code: "FR", name: "FedRAMP", levels: "High · Moderate · Low", desc: "Cloud service authorization program for federal workloads.", href: "https://www.fedramp.gov/" },
  { code: "CMMC", name: "CMMC 2.0", levels: "Level 1 · 2 · 3", desc: "Cybersecurity Maturity Model Certification for the defense industrial base.", href: "https://dodcio.defense.gov/CMMC/" },
  { code: "RMF", name: "Risk Management Framework", levels: "6-Step Lifecycle", desc: "Categorize · Select · Implement · Assess · Authorize · Monitor.", href: "https://csrc.nist.gov/projects/risk-management" },
  { code: "ZT", name: "DoD Zero Trust", levels: "Reference Architecture v2.0", desc: "Seven-pillar Zero Trust target state for DoD enterprise.", href: "https://dodcio.defense.gov/Portals/0/Documents/Library/(U)ZT_RA_v2.0(U)_Sep22.pdf" },
];

const SECTORS = [
  { icon: Building2, t: "Federal Civilian Agencies", d: "Cloud modernization, Zero Trust implementation, and continuous ATO programs for civilian mission systems." },
  { icon: ShieldCheck, t: "Department of Defense", d: "IL4–IL6 cloud foundations, STIG-automated pipelines, and DoD Zero Trust architecture aligned to the Reference Architecture." },
  { icon: Eye, t: "Intelligence Community", d: "Secure development environments, air-gapped CI/CD, and classified workload orchestration." },
  { icon: Factory, t: "Defense Industrial Base", d: "CMMC 2.0 readiness, supply chain security, and compliance program build-out for defense contractors." },
];

const PATHWAYS = [
  { t: "GSA Multiple Award Schedule (MAS)", d: "Multi-Award Schedule for IT services, software, and cloud — broad federal access.", status: "Pursuing" },
  { t: "SEWP", d: "NASA Solutions for Enterprise-Wide Procurement — government-wide IT GWAC.", status: "Pursuing" },
  { t: "CIO-SP3 / OASIS / OASIS+", d: "Professional and IT services GWACs across federal agencies.", status: "Pursuing" },
];

const CASE_OUTCOMES = [
  { n: "01", t: "ATO 6+ months → under 4 weeks", d: "Through automated security controls and continuous evidence collection mapped to NIST 800-53." },
  { n: "02", t: "Daily secure deploys for DoD missions", d: "Delivered STIG-compliant DevSecOps pipelines where monthly releases were the norm." },
  { n: "03", t: "IL5-ready AWS GovCloud foundation", d: "Multi-account Terraform architecture supporting 15+ mission applications with shared compliance inheritance." },
  { n: "04", t: "Zero Trust in 90 days", d: "Implemented identity and micro-segmentation for a federal civilian agency, achieving full NIST 800-207 alignment." },
];

const PILLARS = [
  { name: "User", icon: ShieldCheck, progress: 85 },
  { name: "Device", icon: Lock, progress: 78 },
  { name: "Network", icon: Radar, progress: 82 },
  { name: "Application", icon: Layers, progress: 74 },
  { name: "Data", icon: FileCheck2, progress: 88 },
  { name: "Visibility", icon: Gauge, progress: 80 },
  { name: "Automation", icon: Server, progress: 76 },
];

const Government = () => (
  <>
    <SEO title="Government & Public Sector — Mission-grade technology" description="FedRAMP, NIST 800-53, CMMC 2.0, RMF, and DoD Zero Trust programs engineered into every solution. DevSecOps, cloud, and continuous compliance for federal civilian, DoD, and intelligence community missions." path="/government" />

    <section style={{ paddingTop: 120, paddingBottom: 80 }}>
      <div className="container-grav">
        <div className="eyebrow" style={{ marginBottom: 28 }}><span className="sep" />Government & public sector</div>
        <h1 className="display-xl" style={{ maxWidth: 1200 }}>
          Mission-grade<br />
          <span style={{ color: "var(--green)", fontStyle: "italic", fontWeight: 800 }}>technology.</span>
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" style={{ marginTop: 48 }}>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="lede" style={{ fontWeight: 500, marginBottom: 20 }}>
              FedRAMP, NIST 800-53, CMMC 2.0, RMF, and DoD Zero Trust — engineered into every solution, not bolted on for an audit.
            </p>
            <p className="body-md">
              Gravitas Technology partners with federal civilian agencies, DoD components, and the defense industrial base to deliver secure cloud platforms, automated DevSecOps pipelines, and continuous compliance programs. We build the systems that earn your ATO — and the operational practices that keep it.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Who we serve */}
    <section className="section" style={{ borderTop: "1px solid var(--line)" }}>
      <div className="container-grav">
        <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Who we serve</div>
        <h2 className="display-lg" style={{ maxWidth: 900, marginBottom: 64 }}>Engineered for the most demanding mission environments.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SECTORS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.t} className="tile" style={{ minHeight: 240 }} data-testid={`gov-sector-${i}`}>
                <div className="accent-bar" />
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--green-bg)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size={22} strokeWidth={1.75} />
                </div>
                <h3 className="display-sm" style={{ marginTop: 24 }}>{s.t}</h3>
                <p className="body-md" style={{ marginTop: 12 }}>{s.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Compliance Frameworks */}
    <section className="section" style={{ background: "var(--bg-alt)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div className="container-grav">
        <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Compliance frameworks we engineer against</div>
        <h2 className="display-lg" style={{ maxWidth: 900, marginBottom: 72 }}>Program-grade alignment. Evidence-backed posture.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FRAMEWORKS.map((f) => (
            <a key={f.name} href={f.href} target="_blank" rel="noopener noreferrer" data-testid={`framework-${f.code.toLowerCase()}`} className="tile" style={{ display: "block", textDecoration: "none", color: "var(--ink)", minHeight: 280 }}>
              <div className="accent-bar" />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                <span className="eyebrow" style={{ color: "var(--green)" }}>{f.code}</span>
                <ExternalLink size={16} color="var(--muted)" />
              </div>
              <h3 className="display-sm">{f.name}</h3>
              <div className="eyebrow" style={{ marginTop: 10 }}>{f.levels}</div>
              <p className="body-md" style={{ marginTop: 18 }}>{f.desc}</p>
            </a>
          ))}
        </div>
      </div>
    </section>

    {/* Case outcomes */}
    <section className="section">
      <div className="container-grav">
        <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Government case outcomes</div>
        <h2 className="display-lg" style={{ maxWidth: 900, marginBottom: 64 }}>Delivered for federal and DoD missions.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {CASE_OUTCOMES.map((c) => (
            <div key={c.n} className="tile" style={{ minHeight: 220 }} data-testid={`gov-case-${c.n}`}>
              <div className="accent-bar" />
              <span className="eyebrow">{c.n}</span>
              <h3 className="display-sm" style={{ marginTop: 18 }}>{c.t}</h3>
              <p className="body-md" style={{ marginTop: 12 }}>{c.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Contract Vehicles */}
    <section className="section" style={{ background: "var(--bg-alt)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div className="container-grav">
        <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Contract vehicles</div>
        <h2 className="display-lg" style={{ maxWidth: 900, marginBottom: 24 }}>Pathways we are actively pursuing.</h2>
        <p className="body-md" style={{ maxWidth: 720, marginBottom: 56 }}>
          Gravitas is actively pursuing these procurement pathways. For sole-source, teaming, and subcontracting arrangements available today — please <Link to="/contact" style={{ color: "var(--green)" }}>contact us</Link>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PATHWAYS.map((p, i) => (
            <div key={p.t} className="tile" style={{ minHeight: 220 }}>
              <div className="accent-bar" />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 18 }}>
                <span className="eyebrow">{`0${i + 1}`}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--green)", padding: "4px 10px", border: "1px solid var(--green)", borderRadius: 100, fontWeight: 600 }}>{p.status}</span>
              </div>
              <h3 className="display-sm">{p.t}</h3>
              <p className="body-md" style={{ marginTop: 12 }}>{p.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Zero Trust pillars */}
    <section className="section" style={{ background: "var(--ink)", color: "var(--dark-ink)" }}>
      <div className="container-grav">
        <div className="eyebrow" style={{ color: "rgba(246,244,239,0.6)", marginBottom: 20 }}><span className="sep" style={{ background: "rgba(246,244,239,0.6)" }} />DoD Zero Trust Reference Architecture</div>
        <h2 className="display-lg" style={{ color: "var(--dark-ink)", maxWidth: 900, marginBottom: 64 }}>Seven pillars. One integrated posture.</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.name} style={{ border: "1px solid rgba(246,244,239,0.2)", padding: "20px 24px", display: "grid", gridTemplateColumns: "44px 1fr auto", gap: 20, alignItems: "center" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(13,143,60,0.15)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size={18} strokeWidth={1.75} />
                </div>
                <div>
                  <div className="display-sm" style={{ color: "var(--dark-ink)", fontSize: 18 }}>{p.name}</div>
                  <div style={{ marginTop: 10, height: 4, background: "rgba(246,244,239,0.12)", position: "relative", overflow: "hidden" }}>
                    <div style={{ width: `${p.progress}%`, height: "100%", background: "var(--green)" }} />
                  </div>
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, letterSpacing: "0.1em", color: "var(--green)", fontWeight: 700 }}>{p.progress}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section style={{ padding: "120px 0", background: "var(--green)", color: "#fff" }}>
      <div className="container-grav" style={{ textAlign: "center" }}>
        <h2 className="display-lg" style={{ color: "#fff", maxWidth: 900, margin: "0 auto" }}>Brief our federal team.</h2>
        <p className="lede" style={{ color: "rgba(255,255,255,0.9)", marginTop: 24, maxWidth: 640, margin: "24px auto 0" }}>
          Share your mission context. Our team responds within one business day with scoping options aligned to your contract vehicle and security boundary.
        </p>
        <div style={{ marginTop: 40 }}>
          <Link to="/contact" className="btn btn-light">Request consultation <span className="arrow"><ArrowUpRight size={14} /></span></Link>
        </div>
      </div>
    </section>
  </>
);

export default Government;
